import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AudioClip {
  audioUrl: string | null | undefined;
  text: string;
}

interface UseAudioPlaybackOptions {
  clips: AudioClip[];
  onAllEnded?: () => void;
  onIndexChange?: (index: number) => void;
  initialIndex?: number;
}

export interface AudioPlaybackControls {
  isPlaying: boolean;
  currentIndex: number;
  progress: number;
  lastClipEnded: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  seekTo: (index: number) => void;
  restart: () => void;
}

const hasAudioSrc = (clip: AudioClip | undefined): clip is AudioClip & { audioUrl: string } =>
  Boolean(clip && clip.audioUrl && clip.audioUrl.length > 0);

const speechSupported = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

const INTER_CLIP_GAP_MS = 600;

export function useAudioPlayback({
  clips,
  onAllEnded,
  onIndexChange,
  initialIndex
}: UseAudioPlaybackOptions): AudioPlaybackControls {
  const [currentIndex, setCurrentIndex] = useState(() =>
    typeof initialIndex === 'number' && initialIndex > 0 ? initialIndex : 0
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastClipEnded, setLastClipEnded] = useState(false);

  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const preloadElementRef = useRef<HTMLAudioElement | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const clipsRef = useRef(clips);
  clipsRef.current = clips;
  const onAllEndedRef = useRef(onAllEnded);
  onAllEndedRef.current = onAllEnded;
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const advanceAfterEndedRef = useRef<() => void>(() => {});
  const gapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clipCount = clips.length;

  const stopSpeech = useCallback(() => {
    if (speechSupported()) window.speechSynthesis.cancel();
    utteranceRef.current = null;
  }, []);

  const clearGapTimer = useCallback(() => {
    if (gapTimerRef.current !== null) {
      clearTimeout(gapTimerRef.current);
      gapTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const element = new Audio();
    element.preload = 'auto';
    audioElementRef.current = element;

    const handleTimeUpdate = () => {
      const { duration, currentTime } = element;
      setProgress(duration > 0 ? Math.min(1, currentTime / duration) : 0);
    };
    const handleEnded = () => advanceAfterEndedRef.current();
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    element.addEventListener('timeupdate', handleTimeUpdate);
    element.addEventListener('ended', handleEnded);
    element.addEventListener('play', handlePlay);
    element.addEventListener('pause', handlePause);

    return () => {
      element.removeEventListener('timeupdate', handleTimeUpdate);
      element.removeEventListener('ended', handleEnded);
      element.removeEventListener('play', handlePlay);
      element.removeEventListener('pause', handlePause);
      element.pause();
      element.src = '';
      if (speechSupported()) window.speechSynthesis.cancel();
      if (gapTimerRef.current !== null) clearTimeout(gapTimerRef.current);
    };
  }, []);

  const playIndex = useCallback(
    (index: number, autoStart: boolean) => {
      const element = audioElementRef.current;
      if (!element || index < 0 || index >= clipsRef.current.length) return;

      clearGapTimer();
      stopSpeech();
      setProgress(0);

      const clip = clipsRef.current[index];

      if (hasAudioSrc(clip)) {
        if (element.src !== clip.audioUrl) element.src = clip.audioUrl;
        element.currentTime = 0;
        if (autoStart) {
          void element.play().catch(() => setIsPlaying(false));
        }
      } else if (autoStart && speechSupported() && clip?.text.trim()) {
        element.pause();
        element.removeAttribute('src');
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(clip.text);
        utterance.rate = 1;
        utterance.onstart = () => setIsPlaying(true);
        utterance.onend = () => {
          setIsPlaying(false);
          advanceAfterEndedRef.current();
        };
        utterance.onerror = () => setIsPlaying(false);
        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
      }
    },
    [stopSpeech, clearGapTimer]
  );

  function advanceAfterEnded() {
    const index = currentIndexRef.current;
    setProgress(1);
    if (index >= clipsRef.current.length - 1) {
      setIsPlaying(false);
      setLastClipEnded(true);
      onAllEndedRef.current?.();
      return;
    }
    const nextIndex = index + 1;
    setCurrentIndex(nextIndex);
    onIndexChangeRef.current?.(nextIndex);

    setIsPlaying(true);
    clearGapTimer();
    gapTimerRef.current = setTimeout(() => {
      gapTimerRef.current = null;
      playIndex(nextIndex, true);
    }, INTER_CLIP_GAP_MS);
  }
  advanceAfterEndedRef.current = advanceAfterEnded;

  useEffect(() => {
    const nextClip = clips[currentIndex + 1];
    if (!hasAudioSrc(nextClip)) return;
    const preloader = preloadElementRef.current ?? new Audio();
    preloader.preload = 'auto';
    preloader.src = nextClip.audioUrl;
    preloader.load();
    preloadElementRef.current = preloader;
  }, [clips, currentIndex]);

  const play = useCallback(() => {
    clearGapTimer();
    const element = audioElementRef.current;
    const clip = clipsRef.current[currentIndexRef.current];
    if (element && hasAudioSrc(clip)) {
      if (element.src !== clip.audioUrl) element.src = clip.audioUrl;
      void element.play().catch(() => setIsPlaying(false));
    } else {
      playIndex(currentIndexRef.current, true);
    }
  }, [playIndex, clearGapTimer]);

  const pause = useCallback(() => {
    clearGapTimer();
    audioElementRef.current?.pause();
    stopSpeech();
    setIsPlaying(false);
  }, [stopSpeech, clearGapTimer]);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, pause, play]);

  const seekTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= clipsRef.current.length) return;
      const wasPlaying = isPlaying;
      setCurrentIndex(index);
      setLastClipEnded(false);
      onIndexChangeRef.current?.(index);
      playIndex(index, wasPlaying);
    },
    [isPlaying, playIndex]
  );

  const next = useCallback(() => {
    seekTo(Math.min(currentIndexRef.current + 1, clipsRef.current.length - 1));
  }, [seekTo]);

  const prev = useCallback(() => {
    seekTo(Math.max(currentIndexRef.current - 1, 0));
  }, [seekTo]);

  const restart = useCallback(() => {
    clearGapTimer();
    setLastClipEnded(false);
    const element = audioElementRef.current;
    if (element && hasAudioSrc(clipsRef.current[currentIndexRef.current])) {
      element.currentTime = 0;
      void element.play().catch(() => setIsPlaying(false));
    } else {
      playIndex(currentIndexRef.current, true);
    }
  }, [playIndex, clearGapTimer]);

  useEffect(() => {
    if (currentIndex > clipCount - 1 && clipCount > 0) setCurrentIndex(clipCount - 1);
  }, [clipCount, currentIndex]);

  return useMemo(
    () => ({
      isPlaying,
      currentIndex,
      progress,
      lastClipEnded,
      play,
      pause,
      toggle,
      next,
      prev,
      seekTo,
      restart
    }),
    [
      isPlaying,
      currentIndex,
      progress,
      lastClipEnded,
      play,
      pause,
      toggle,
      next,
      prev,
      seekTo,
      restart
    ]
  );
}
